<script setup>
import useVuelidate from '@vuelidate/core';
import { computed, onMounted, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { constructParameterRule } from './helpers/validations';
import ComboBox from 'next/combobox/ComboBox.vue';
import InputNext from 'next/input/Input.vue';
import TextAreaNext from 'next/textarea/TextArea.vue';

const props = defineProps({
  parameter: {
    type: Object,
    required: true,
  },
  modelValue: {
    type: [String, Number, Boolean, null],
    default: null,
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const initialState = {
  updatedValue: props.modelValue,
};

const state = reactive({ ...initialState });

const rules = {
  updatedValue: constructParameterRule(props.parameter),
};

const v$ = useVuelidate(rules, state);

const fieldLabel = computed(() => {
  return props.parameter.required
    ? `${props.parameter.label} *`
    : props.parameter.label;
});

const errorText = computed(() => {
  const $updatedValue = v$.value.updatedValue;
  if (!$updatedValue.$error) return null;

  if ($updatedValue.url?.$invalid)
    return t(
      'WGPT_BOTS.NEW_FLOW.CREATE.FORM.TEMPLATE.PARAMETERS.ERRORS.INVALID_URL'
    );

  return t(
    'WGPT_BOTS.NEW_FLOW.CREATE.FORM.TEMPLATE.PARAMETERS.ERRORS.REQUIRED'
  );
});

const castParameterValue = value => {
  if (props.parameter.type === 'number') {
    if (value === '') return null;
    return Number(value);
  }
  return `${value ?? ''}`;
};

const updateValue = value => {
  emit('update:modelValue', castParameterValue(value));
};

const setDefaults = () => {
  if (
    state.updatedValue !== null &&
    state.updatedValue !== undefined &&
    state.updatedValue !== ''
  )
    return;
  state.updatedValue = props.parameter.default || '';
};

watch(state, value => {
  updateValue(value.updatedValue);
});

onMounted(() => {
  setDefaults();
});
</script>

<template>
  <div>
    <label v-if="parameter.type === 'select'" class="block">
      {{ fieldLabel }}
      <ComboBox
        v-model="state.updatedValue"
        :options="parameter.options"
        :has-error="v$.updatedValue.$error"
        :class="{
          '[&>div>button]:!outline-n-ruby-8 [&>div>button:not(.focused)]:hover:!outline-n-ruby-9':
            v$.updatedValue.$error,
        }"
        @change="v$.updatedValue.$touch"
      />
    </label>

    <TextAreaNext
      v-else-if="parameter.type === 'textarea'"
      v-model="state.updatedValue"
      :label="fieldLabel"
      class="w-full"
      resize
      custom-text-area-class="!outline !outline-0"
      :message-type="v$.updatedValue.$error ? 'error' : 'info'"
      @blur="v$.updatedValue.$touch"
    />

    <InputNext
      v-else-if="parameter.type === 'number'"
      v-model="state.updatedValue"
      :label="fieldLabel"
      type="number"
      class="w-full"
      :message-type="v$.updatedValue.$error ? 'error' : null"
      @blur="v$.updatedValue.$touch"
    />

    <InputNext
      v-else
      v-model="state.updatedValue"
      :label="fieldLabel"
      class="w-full"
      :message-type="v$.updatedValue.$error ? 'error' : null"
      @blur="v$.updatedValue.$touch"
    />

    <p
      v-if="errorText"
      class="min-w-0 mt-1 mb-0 text-xs truncate transition-all duration-500 ease-in-out text-n-ruby-9 dark:text-n-ruby-9"
    >
      {{ errorText }}
    </p>
  </div>
</template>
